import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SolicitarDemoPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    cargo: '',
    numeroFuncionarios: '',
    dataPreferencial: '',
    horarioPreferencial: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.currentTarget;
      const formDataToSend = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch(formElement.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          empresa: '',
          cargo: '',
          numeroFuncionarios: '',
          dataPreferencial: '',
          horarioPreferencial: '',
          mensagem: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="font-medium">Voltar</span>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl mb-6 shadow-xl">
            <i className="ri-calendar-check-line text-4xl text-white"></i>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Solicite uma Demonstração
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Agende uma apresentação personalizada e descubra como o Área do Premiado pode transformar a gestão de incentivos da sua empresa
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <form 
              id="demo-request-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d4lk3h232n60g3bh7v3g"
              method="POST"
              onSubmit={handleSubmit}
              className="space-y-8"
            >
              {/* Informações Pessoais */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-user-line text-orange-600"></i>
                  </div>
                  Suas Informações
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none text-sm"
                      placeholder="João Silva"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      E-mail Corporativo *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none text-sm"
                      placeholder="joao@empresa.com.br"
                    />
                  </div>
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none text-sm"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <label htmlFor="cargo" className="block text-sm font-semibold text-gray-700 mb-2">
                      Cargo *
                    </label>
                    <input
                      type="text"
                      id="cargo"
                      name="cargo"
                      value={formData.cargo}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none text-sm"
                      placeholder="Gerente de RH"
                    />
                  </div>
                </div>
              </div>

              {/* Informações da Empresa */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-building-line text-orange-600"></i>
                  </div>
                  Sobre sua Empresa
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="empresa" className="block text-sm font-semibold text-gray-700 mb-2">
                      Nome da Empresa *
                    </label>
                    <input
                      type="text"
                      id="empresa"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none text-sm"
                      placeholder="Empresa LTDA"
                    />
                  </div>
                  <div>
                    <label htmlFor="numeroFuncionarios" className="block text-sm font-semibold text-gray-700 mb-2">
                      Número de Funcionários *
                    </label>
                    <select
                      id="numeroFuncionarios"
                      name="numeroFuncionarios"
                      value={formData.numeroFuncionarios}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none text-sm cursor-pointer"
                    >
                      <option value="">Selecione</option>
                      <option value="1-10">1 a 10 funcionários</option>
                      <option value="11-50">11 a 50 funcionários</option>
                      <option value="51-200">51 a 200 funcionários</option>
                      <option value="201-500">201 a 500 funcionários</option>
                      <option value="501-1000">501 a 1000 funcionários</option>
                      <option value="1000+">Mais de 1000 funcionários</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Agendamento */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-calendar-line text-orange-600"></i>
                  </div>
                  Preferência de Agendamento
                </h2>
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 mb-6">
                  <div className="flex items-start gap-3 mb-4">
                    <i className="ri-information-line text-orange-600 text-xl mt-1"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        Sugestão de Horários
                      </p>
                      <p className="text-sm text-gray-600">
                        Nossa equipe está disponível de segunda a sexta-feira, das 9h às 18h. As demonstrações têm duração média de 30 a 45 minutos.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-xs font-semibold text-gray-500 mb-1">Manhã</div>
                      <div className="text-sm font-bold text-gray-900">9h - 12h</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-xs font-semibold text-gray-500 mb-1">Almoço</div>
                      <div className="text-sm font-bold text-gray-900">12h - 14h</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-xs font-semibold text-gray-500 mb-1">Tarde</div>
                      <div className="text-sm font-bold text-gray-900">14h - 17h</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-xs font-semibold text-gray-500 mb-1">Final</div>
                      <div className="text-sm font-bold text-gray-900">17h - 18h</div>
                    </div>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="dataPreferencial" className="block text-sm font-semibold text-gray-700 mb-2">
                      Data Preferencial *
                    </label>
                    <input
                      type="date"
                      id="dataPreferencial"
                      name="dataPreferencial"
                      value={formData.dataPreferencial}
                      onChange={handleChange}
                      min={getMinDate()}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none text-sm cursor-pointer"
                    />
                  </div>
                  <div>
                    <label htmlFor="horarioPreferencial" className="block text-sm font-semibold text-gray-700 mb-2">
                      Horário Preferencial *
                    </label>
                    <select
                      id="horarioPreferencial"
                      name="horarioPreferencial"
                      value={formData.horarioPreferencial}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none text-sm cursor-pointer"
                    >
                      <option value="">Selecione um horário</option>
                      <option value="09:00">09:00</option>
                      <option value="09:30">09:30</option>
                      <option value="10:00">10:00</option>
                      <option value="10:30">10:30</option>
                      <option value="11:00">11:00</option>
                      <option value="11:30">11:30</option>
                      <option value="12:00">12:00</option>
                      <option value="12:30">12:30</option>
                      <option value="13:00">13:00</option>
                      <option value="13:30">13:30</option>
                      <option value="14:00">14:00</option>
                      <option value="14:30">14:30</option>
                      <option value="15:00">15:00</option>
                      <option value="15:30">15:30</option>
                      <option value="16:00">16:00</option>
                      <option value="16:30">16:30</option>
                      <option value="17:00">17:00</option>
                      <option value="17:30">17:30</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Mensagem Adicional */}
              <div>
                <label htmlFor="mensagem" className="block text-sm font-semibold text-gray-700 mb-2">
                  Mensagem Adicional (Opcional)
                </label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all outline-none resize-none text-sm"
                  placeholder="Conte-nos mais sobre suas necessidades e expectativas..."
                ></textarea>
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.mensagem.length}/500 caracteres
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white text-lg font-bold rounded-xl hover:shadow-2xl hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line text-2xl animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-2xl"></i>
                      Solicitar Demonstração
                    </>
                  )}
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
                  <div>
                    <p className="font-semibold text-green-900 mb-1">Solicitação enviada com sucesso!</p>
                    <p className="text-sm text-green-700">
                      Nossa equipe entrará em contato em breve para confirmar o agendamento da demonstração.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <i className="ri-error-warning-fill text-red-600 text-2xl"></i>
                  <div>
                    <p className="font-semibold text-red-900 mb-1">Erro ao enviar solicitação</p>
                    <p className="text-sm text-red-700">
                      Por favor, tente novamente ou entre em contato conosco diretamente.
                    </p>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Benefits Section */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Demonstração Personalizada</h3>
              <p className="text-sm text-gray-600">
                Apresentação focada nas necessidades específicas da sua empresa
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-question-answer-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Tire suas Dúvidas</h3>
              <p className="text-sm text-gray-600">
                Especialistas prontos para responder todas as suas perguntas
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-rocket-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Sem Compromisso</h3>
              <p className="text-sm text-gray-600">
                Conheça a plataforma sem nenhuma obrigação de contratação
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
